package com.crowpay.views.components.project.work.changeOrders.mobile

import com.crowpay.ChangeRequest
import com.crowpay.ChangeRequestItem
import com.crowpay.Formats
import com.crowpay.HasDisplayName
import com.crowpay.LineItem
import com.crowpay.LineItemInterface
import com.crowpay.PendingItemChange
import com.crowpay.PendingLineItem
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.renderer
import com.crowpay.format
import com.crowpay.linked
import com.crowpay.pending
import com.crowpay.sdk.notFoundError
import com.crowpay.utils.BulletPoint
import com.crowpay.utils.JumpTo
import com.crowpay.utils.SignalingSet
import com.crowpay.utils.containsAny
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.resize
import com.crowpay.views.components.forEachWithRenderer
import com.crowpay.views.components.keyString
import com.crowpay.views.components.keyView
import com.crowpay.views.components.label2
import com.crowpay.views.components.labeledSection
import com.crowpay.views.components.priceWithChange
import com.crowpay.views.components.project.work.changeOrders.ChangeStatus
import com.crowpay.views.components.project.work.changeOrders.approveSelected
import com.crowpay.views.components.project.work.changeOrders.denySelected
import com.crowpay.views.components.project.work.changeOrders.status
import com.crowpay.views.components.project.work.scope.LineItemDetailsPage
import com.crowpay.views.components.renderPriceChange
import com.crowpay.views.dialogs.ChangesRejectedDetails
import com.crowpay.views.screens.common.AuthorizedProjectView
import com.crowpay.views.screens.common.HasProjectData
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.assertViewerLens
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.ApprovedSemantic
import com.crowpay.views.theming.DeniedSemantic
import com.crowpay.views.theming.DisplayableStateSemantic
import com.crowpay.views.theming.InnerTabBar
import com.crowpay.views.theming.LightSection
import com.crowpay.views.theming.ListSemantic
import com.crowpay.views.theming.ReviewSelectionSemantic
import com.crowpay.views.theming.SectionSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.colored
import com.crowpay.views.theming.dangerButton
import com.crowpay.views.theming.darkSection
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.CardSemantic
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.div
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.models.times
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.equalTo
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.lens
import com.lightningkite.kiteui.reactive.reactive
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.reactive.withWrite
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atBottomEnd
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.RowOrCol
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.checkbox
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.link
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.radioToggleButton
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.toggleButton
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.italic
import com.lightningkite.kiteui.views.l2.RecyclerViewRenderer
import com.lightningkite.kiteui.views.l2.RecyclerViewRendererSet
import com.lightningkite.kiteui.views.l2.icon
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.minutes

@Routable("project/{projectId}/change/{changeId}/{lens}")
class ChangeOrderDetails(
    override val projectId: UUID,
    val changeId: UUID,
    val lens: ProjectLens,
    parent: HasProjectData? = null
) : Page, HasProjectData by (parent ?: AuthorizedProjectView.Data(lens, projectId)) {

    val changeRequest = shared { changeOrders().find { it._id == changeId } ?: notFoundError<ChangeRequest>() }

    private val comparator = compareBy<ChangeRequestItem>({ !it.linked }, { it.itemNumber })
    val changeItems = shared<List<ChangeRequestItem>> {
        (pendingItemChanges().filter { it.changeRequest == changeId } +
                pendingLineItems().filter { it.changeRequest == changeId }).sortedWith(comparator)
    }

    val linkedIds = shared { changeItems().filter { it.linked }.map { it._id } }

    val status = shared { changeRequest().status(changeItems()) }

    val changeRejectedDetails = shared {
        { ChangesRejectedDetails(changeRequest(), changeItems(), client().name, contractor().name) }
    }

    val selectedItems = SignalingSet<UUID>()

    override val title: Readable<String> = shared { "Change #${changeRequest().number}" }

    private enum class Section(override val displayName: String) : HasDisplayName {
        Info("Info"),
        Items("Items")
    }

    private val selectedSection = Property(Section.Items)

    override fun ViewWriter.render2(): ViewModifiable = col {
        launch { assertViewerLens(lens) }

        expanding - scrolls - col {
            spacing = AppDimensions.majorColumnSpacing

            col {
                lightSection - col {
                    title { ::content { changeRequest().let { "Change #${it.number}: ${it.title}" } } }

                    label2("Status") {
                        title {
                            dynamicTheme { DisplayableStateSemantic(status()) }
                            ::content { status().displayName }
                        }
                    }
                }
            }

            InnerTabBar.onNext - row {
                for (section in Section.entries) {
                    expanding - buttonTheme - radioToggleButton {
                        centered - body(section.displayName)
                        checked bind selectedSection.equalTo(section)
                    }
                }
            }

            stack {
                reactive {
                    clearChildren()
                    when (selectedSection()) {
                        Section.Info -> infoSection()
                        Section.Items -> itemsSection()
                    }
                }
            }
        }

        darkSection - row {
            spacing = AppDimensions.buttonRowSpacing
            expanding - space()
            actionBar()
        }
    }

    private fun ViewWriter.infoSection() = col {
        labeledSection("Description") {
            body { ::content { changeRequest().description } }
        }
        labeledSection("Summary") {
            col {
                keyString("Cardinal") { if (changeRequest().cardinalChange) "Yes" else "No" }
                keyString("Submitted") { changeRequest().published?.format(Formats.mmddyyyy_hmm) ?: "" }
                keyView("Change Amount") {
                    renderPriceChange(false) { changeItems().sumOf { it.priceChange ?: 0 } }
                }
            }
        }
    }

    private fun ViewWriter.itemsSection() = col {
        val pending = changeItems.lens { it.filter { it.pending } }
        val reviewed = changeItems.lens { it.filterNot { it.pending } }

        onlyWhen { pending().isNotEmpty() } - ReviewSelectionSemantic.onNext - label2("Pending Items") {
            if (lens.isClient()) spacing = AppDimensions.labelSpacing * 2
            val renderers = ChangeItemCards(approving = true)
            if (lens.isClient()) lightSection - compact - toggleButton {
                val allSelected = shared {
                    selectedItems().containsAll(changeItems().filter { it.pending }.map { it._id })
                }.withWrite { sel ->
                    val ids = changeItems().filter { it.pending }.map { it._id }
                    if (sel) selectedItems.addAll(ids)
                    else selectedItems.removeAll(ids)
                }
                checked bind allSelected
                row {
                    centered - checkbox {
                        checked bind allSelected
                    }
                    centered - body("Select All")
                }
            }
            ListSemantic.onNext - col {
                forEachWithRenderer(pending, renderers)
            }
        }

        onlyWhen { reviewed().isNotEmpty() } - label2("Reviewed Items") {
            val renderers = ChangeItemCards(approving = false)
            ListSemantic.onNext - col {
                forEachWithRenderer(reviewed, renderers)
            }
        }
    }


    private fun ViewWriter.actionBar() = row {
        spacing = AppDimensions.buttonRowSpacing
        val pending = changeItems.lens { it.filter { it.pending } }
        val allSelected = shared { pending().all { selectedItems().contains(it._id) } && pending().isNotEmpty() }
        val labelValue = shared {
            if (allSelected()) " All"
            else if (selectedItems().size > 1) " (${selectedItems.size})"
            else ""
        }
        val canApprove = shared { lens == ProjectLens.Customer && pending().isNotEmpty() && selectedItems().isNotEmpty() }
        val canReject = shared { lens == ProjectLens.Customer && pending().isNotEmpty() && selectedItems().isNotEmpty() }

        if (lens == ProjectLens.Customer) {
            dangerButton - button {
                existsDefaultFalse { canReject() }
                specCenteredText { "Reject${labelValue()}" }
                action = denySelected { changeItems().filter { it._id in selectedItems } }
            }
            primaryButton - button {
                existsDefaultFalse { canApprove() }
                specCenteredText { "Approve${labelValue()}" }
                action = approveSelected { changeItems().filter { it._id in selectedItems } }
            }
        }
        if (lens == ProjectLens.Contractor) secondaryButton - button {
            ::exists { changeItems().any { it.status == ChangeStatus.Rejected } }
            specCenteredText("View Concerns")
            onClick {
                mainScreenNavigator.navigate(changeRejectedDetails()())
            }
        }
    }

    inner class ChangeItemCards(val approving: Boolean) : RecyclerViewRendererSet<ChangeRequestItem, UUID> {

        private fun ViewWriter.itemCard(item: Readable<ChangeRequestItem>, otherInfo: RowOrCol.(Readable<LineItemInterface>) -> Unit): ViewModifiable {
            val status = shared { item().status }

            val line = shared {
                val change = item()
                if (change is LineItemInterface) change
                else lineItems()
                    .find { item -> item._id == change.itemId }
                    ?.let { item ->
                        val cutoff = change.changeAccepted ?: change.changeRejected
                        if (cutoff == null) item
                        else item.excludeChangesAfter(cutoff - 1.minutes, emptyList(), null)
                            ?: object : LineItemInterface by item {
                                override val price: Long = 0
                            }
                    }
                    ?: notFoundError<LineItem>()
            }

            fun ViewWriter.content() = col {
                spacing = AppDimensions.labelSpacing * 2

                launch {
                    JumpTo.ChangeItem.newTarget(item()._id, this@col)
                }

                row {
                    expanding - col {
                        spacing = AppDimensions.labelSpacing
                        italic - row {
                            spacing = AppDimensions.labelSpacing
                            if (approving) {
                                onlyWhen { item().linked } - colored(AppColors.secondary.main) - icon(Icon.linked.resize(1.rem), "Linked")
                            }
                            centered - body { ::content { item().itemNumber } }
                            centered - body(BulletPoint)
                            centered - body {
                                ::content {
                                    item().changeType.let {
                                        if (status() == ChangeStatus.Accepted) it.postTense
                                        else it.preTense
                                    }
                                }
                            }
                        }
                        bodyBold { ::content { line().name } }
                    }
                    atBottomEnd - bodyBold {
                        dynamicTheme { DisplayableStateSemantic(status()) }
                        ::content { status().displayName }
                    }
                }
                greySeparator()
                otherInfo(line)
            }

            return if (approving) {
                val isSelected = shared {
                    val it = item()
                    if (it.linked) selectedItems().containsAny(linkedIds())
                    else selectedItems().contains(it._id)
                }.withWrite { sel ->
                    val it = item()
                    if (it.linked) {
                        if (sel) selectedItems.addAll(linkedIds())
                        else selectedItems.removeAll(linkedIds())
                    } else {
                        if (sel) selectedItems.add(it._id)
                        else selectedItems.remove(it._id)
                    }
                }

                if (lens.isClient()) row {
                    spacing = AppDimensions.labelSpacing / 2

                    compact - toggleButton {
                        dynamicTheme {
                            if (item().linked) ApprovedSemantic + CardSemantic
                            else SectionSemantic
                        }
                        centered - checkbox { checked bind isSelected }
                        checked bind isSelected
                    }
                    expanding - lightSection - toggleButton {
                        content()
                        checked bind isSelected
                    }
                }
                else lightSection - content()
            }
            else link {
                dynamicTheme {
                    when (status()) {
                        ChangeStatus.Accepted -> ApprovedSemantic + CardSemantic
                        ChangeStatus.Rejected -> DeniedSemantic + CardSemantic
                        ChangeStatus.PendingApproval -> LightSection
                    }
                }
                content()
                ::to {
                    when (status()) {
                        ChangeStatus.PendingApproval -> null
                        ChangeStatus.Rejected -> changeRejectedDetails()
                        ChangeStatus.Accepted -> {
                            { LineItemDetailsPage(projectId, item().itemId, lens, this@ChangeOrderDetails) }
                        }
                    }
                }
                onNavigate {
                    when (status()) {
                        ChangeStatus.PendingApproval,
                        ChangeStatus.Accepted -> {}
                        ChangeStatus.Rejected -> JumpTo.ChangeItem(item()._id)
                    }
                }
            }
        }

        val newItem = renderer { item: Readable<PendingLineItem>, _ ->
            itemCard(item) {
                col {
                    keyView("Price") {
                        renderPriceChange(false) { item().price }
                    }
                    onlyWhen { item().description.isNotBlank() } - label2("Description") {
                        body { ::content { item().description } }
                    }
                }
            }
        }

        val itemChange = renderer { item: Readable<PendingItemChange>, _ ->
            itemCard(item) { line ->
                col {
                    onlyWhen { item().priceChange != null } - col {
                        keyView("Price Change") {
                            renderPriceChange(false) { item().priceChange }
                        }
                        keyView("New Price") {
                            priceWithChange(
                                addPadding = false,
                                price = { line().price + (item().priceChange ?: 0) },
                                change = { item().priceChange }
                            )
                        }
                    }
                    keyString("Updated Description") { item().updatedDescription }
                }
            }
        }

        val cancelledItem = renderer { item: Readable<PendingItemChange>, _ ->
            itemCard(item) { line ->
                keyView("Price Change") {
                    renderPriceChange(false) { -line().price }
                }
            }
        }

        override fun id(item: ChangeRequestItem): UUID = item._id

        override fun renderer(item: ChangeRequestItem): RecyclerViewRenderer<ChangeRequestItem> = when (item) {
            is PendingLineItem -> newItem
            is PendingItemChange -> if (item.cancelled) cancelledItem else itemChange
            else -> throw IllegalStateException("Unsupported ChangeRequestItem: $item")
        }
    }
}